import { debounce, DebouncedFunc, DebounceSettings } from "lodash-es";

// eslint-disable-next-line max-lines-per-function
export function debounceWithTracking<T extends AnyFunction>(
  func: T,
  wait?: number,
  options?: DebounceSettings,
): DebouncedFunc<T> & Tracking {
  let invocations = 0;
  let unsettled = 0;
  let isWaiting = false;
  // Same reading as lodash: an explicit `trailing: undefined` disables the trailing edge.
  const hasTrailingEdge =
    options && "trailing" in options ? Boolean(options.trailing) : true;

  const settle = (): void => {
    unsettled -= 1;
  };

  const tracked = (...args: Parameters<T>): ReturnType<T> => {
    invocations += 1;
    isWaiting = false;
    unsettled += 1;
    let result: ReturnType<T>;
    try {
      result = func(...args);
    } catch (error) {
      settle();
      throw error;
    }
    return notifySettled(result, settle);
  };

  const debounced = debounce(tracked, wait, options);

  const call = (...args: Parameters<T>): ReturnType<T> | undefined => {
    const invocationsBefore = invocations;
    const result = debounced(...args);
    // A call that did not invoke synchronously is dropped unless a trailing edge picks it up.
    if (invocations === invocationsBefore && hasTrailingEdge) isWaiting = true;
    return result;
  };

  return Object.assign(call, {
    cancel: (): void => {
      debounced.cancel();
      isWaiting = false;
    },
    flush: (): ReturnType<T> | undefined => debounced.flush(),
    hasPending: (): boolean => isWaiting || unsettled > 0,
  });
}

export type Tracking = {
  hasPending: () => boolean;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFunction = (...args: any[]) => any;

function notifySettled<R>(result: R, onSettled: () => void): R {
  if (!isPromiseLike(result)) {
    onSettled();
    return result;
  }
  // Hand back the derived promise, not the original: observing the original here would mark
  // a rejection as handled, so a failed trailing invocation nobody awaits would go unreported.
  return result.then(
    (value: unknown) => {
      onSettled();
      return value;
    },
    (error: unknown) => {
      onSettled();
      throw error;
    },
  ) as R;
}

function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    "then" in value &&
    typeof value.then === "function"
  );
}
